import { Navbar } from "../components/layout/Navbar";
import { Footer } from "../components/layout/Footer";
import { Button } from "../components/ui/button";
import { Mail, Linkedin, Briefcase } from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";
import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";

const Experts = () => {
  const { t, language } = useLanguage();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleApplyNow = () => {
    setIsModalOpen(true);
  };

  // Translated experts data
  const experts = [
    {
      id: 1,
      name: t('experts.expertsList.khalid.name'),
      title: t('experts.expertsList.khalid.title'),
      specialty: t('experts.expertsList.khalid.specialty'),
      experience: t('experts.expertsList.khalid.experience'),
      avatar: t('experts.expertsList.khalid.name')[0],
    },
    {
      id: 2,
      name: t('experts.expertsList.noura.name'),
      title: t('experts.expertsList.noura.title'),
      specialty: t('experts.expertsList.noura.specialty'),
      experience: t('experts.expertsList.noura.experience'),
      avatar: t('experts.expertsList.noura.name')[0],
    },
    {
      id: 3,
      name: t('experts.expertsList.fahad.name'),
      title: t('experts.expertsList.fahad.title'),
      specialty: t('experts.expertsList.fahad.specialty'),
      experience: t('experts.expertsList.fahad.experience'),
      avatar: t('experts.expertsList.fahad.name')[0],
    },
    {
      id: 4,
      name: t('experts.expertsList.sara.name'),
      title: t('experts.expertsList.sara.title'),
      specialty: t('experts.expertsList.sara.specialty'),
      experience: t('experts.expertsList.sara.experience'),
      avatar: t('experts.expertsList.sara.name')[0],
    },
    {
      id: 5,
      name: t('experts.expertsList.abdullah.name'),
      title: t('experts.expertsList.abdullah.title'),
      specialty: t('experts.expertsList.abdullah.specialty'),
      experience: t('experts.expertsList.abdullah.experience'),
      avatar: t('experts.expertsList.abdullah.name')[0],
    },
    {
      id: 6,
      name: t('experts.expertsList.reem.name'),
      title: t('experts.expertsList.reem.title'),
      specialty: t('experts.expertsList.reem.specialty'),
      experience: t('experts.expertsList.reem.experience'),
      avatar: t('experts.expertsList.reem.name')[0],
    },
  ];

  return (
    <div className="min-h-screen" dir={language === "ar" ? "rtl" : "ltr"}>
      <Navbar />
      <main>
        {/* Hero */}
        <section className="py-20 bg-hero-gradient text-primary-foreground">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center">
              <span className="text-sm font-semibold text-primary-foreground uppercase tracking-wide animate-fade-in-down" style={{ animationDelay: '0.1s' }}>
                {t('experts.hero.sectionTitle')}
              </span>
              <h1 className="text-4xl md:text-5xl font-bold mt-3 mb-6 animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
                {t('experts.hero.title')}
              </h1>
              <p className="text-lg text-primary-foreground/80 leading-relaxed animate-fade-in-up" style={{ animationDelay: '0.4s' }}>
                {t('experts.hero.subtitle')}
              </p>
            </div>
          </div>
        </section>

        {/* Experts Grid */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {experts.map((expert) => (
                <div
                  key={expert.id}
                  className="bg-card rounded-2xl p-8 shadow-card hover:shadow-elevated transition-all duration-300 group text-center"
                >
                  {/* Avatar */}
                  <div className="w-24 h-24 rounded-full bg-hero-gradient flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform">
                    <span className="text-primary-foreground text-3xl font-bold">
                      {expert.avatar}
                    </span>
                  </div>

                  {/* Info */}
                  <h3 className="text-xl font-bold mb-1">{expert.name}</h3>
                  <p className="text-primary font-medium mb-3">{expert.title}</p>
                  <p className="text-muted-foreground text-sm mb-4">{expert.specialty}</p>

                  {/* Experience Badge */}
                  <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-muted text-sm mb-6">
                    <Briefcase className="w-4 h-4 text-primary" />
                    <span>{expert.experience} {t('experts.expertsList.khalid.experience').includes('years') ? t('experts.expertsList.khalid.experience').split(' ')[1] : t('common.experience')}</span>
                  </div>

                  {/* Actions */}
                  <div className="flex justify-center gap-3">
                    <Button variant="outline" size="icon" className="rounded-full">
                      <Mail className="w-4 h-4" />
                    </Button>
                    <Button variant="outline" size="icon" className="rounded-full">
                      <Linkedin className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 bg-sidebar text-sidebar-foreground">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              {t('experts.cta.title')}
            </h2>
            <p className="text-sidebar-foreground/70 mb-8 max-w-2xl mx-auto">
              {t('experts.cta.description')}
            </p>
            <Button 
              size="lg" 
              className="bg-sidebar-primary text-sidebar-primary-foreground hover:bg-sidebar-primary/90"
              onClick={handleApplyNow}
            >
              {t('experts.cta.applyNow')}
            </Button>
          </div>
        </section>
      </main>
      <Footer />

      {/* Application Form Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t('experts.modal.title')}</DialogTitle>
            <DialogDescription>
              {t('experts.modal.description')}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <form className="space-y-4">
              <div>
                <label htmlFor="name" className="block text-sm font-medium mb-1">
                  {t('experts.modal.name')}
                </label>
                <Input id="name" placeholder={t('experts.modal.namePlaceholder')} />
              </div>
              <div>
                <label htmlFor="email" className="block text-sm font-medium mb-1">
                  {t('experts.modal.email')}
                </label>
                <Input id="email" type="email" placeholder={t('experts.modal.emailPlaceholder')} />
              </div>
              <div>
                <label htmlFor="specialty" className="block text-sm font-medium mb-1">
                  {t('experts.modal.specialty')}
                </label>
                <Input id="specialty" placeholder={t('experts.modal.specialtyPlaceholder')} />
              </div>
              <div>
                <label htmlFor="experience" className="block text-sm font-medium mb-1">
                  {t('experts.modal.experience')}
                </label>
                <Input id="experience" placeholder={t('experts.modal.experiencePlaceholder')} />
              </div>
              <div>
                <label htmlFor="message" className="block text-sm font-medium mb-1">
                  {t('experts.modal.message')}
                </label>
                <Textarea id="message" placeholder={t('experts.modal.messagePlaceholder')} rows={4} />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setIsModalOpen(false)}
                >
                  {t('common.cancel')}
                </Button>
                <Button type="submit">
                  {t('experts.modal.submit')}
                </Button>
              </div>
            </form>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Experts;